import { binarySearchPosition, findTextOffset, findNodeAtOffset } from 'app/components/call/SharedSelection/dom';
import { INodePoint } from 'app/components/call/SharedSelection/LazySortedNodes';
import { CursorPosition } from '@engoo/realtime-protocol/selection';

export interface ISelectionNodesIndices {
  startIndex: number;
  startOffset: number;
  endIndex: number;
  endOffset: number;
}

export function findSelectedNodesIndices(
  { startNode, endNode, ...selection }: ISelectionRange,
  nodes: readonly INodePoint[],
): ISelectionNodesIndices | null {
  const start = binarySearchPosition(nodes, startNode);
  const end = binarySearchPosition(nodes, endNode);
  const startIndex = start.match !== undefined ? start.match! : start.before;
  const endIndex = end.match !== undefined ? end.match! : end.after;

  let startOffset = 0;
  let endOffset = -1;

  if (startIndex === undefined || endIndex === undefined) {
    return null;
  }

  if (startIndex === start.match) {
    const startPoint = nodes[startIndex];

    if (!startPoint.block) {
      startOffset = selection.startOffset + findTextOffset(startPoint.node, startNode);
    }
  }

  if (endIndex === end.match) {
    const endPoint = nodes[endIndex];

    if (!endPoint.block) {
      endOffset = selection.endOffset + findTextOffset(endPoint.node, endNode);
    }
  }

  return {
    startIndex,
    startOffset,
    endIndex,
    endOffset,
  };
}

export interface ISelectionRange {
  startNode: Node;
  startOffset: number;
  endNode: Node;
  endOffset: number;
  cursorPosition: CursorPosition;
}

/**
 * Helper type to indicate only used parts. Simplifies writing tests.
 */
type RangeSubset = Pick<Range, 'startContainer' | 'endContainer' | 'startOffset' | 'endOffset'>;

/**
 * Helper type to indicate only used parts. Simplifies writing tests.
 */
type SelectionSubset = Pick<
  Selection,
  'anchorNode' | 'anchorOffset' | 'focusNode' | 'focusOffset' | 'rangeCount'
> & {
  getRangeAt: (index: number) => RangeSubset;
};

export function getSelectionRange(selection: SelectionSubset | null): ISelectionRange | null {
  if (!selection || selection.rangeCount < 1) return null;

  const { anchorNode, anchorOffset, focusNode, focusOffset } = selection;
  const startRange = selection.getRangeAt(0);
  const endRange = selection.getRangeAt(selection.rangeCount - 1);

  const start = findNodeAtOffset(startRange.startContainer, startRange.startOffset) || {
    node: startRange.startContainer,
    offset: startRange.startOffset,
  };

  const end = findNodeAtOffset(endRange.endContainer, endRange.endOffset) || {
    node: endRange.endContainer,
    offset: endRange.endOffset,
  };

  return {
    startNode:   start.node,
    startOffset: start.offset,
    endNode:     end.node,
    endOffset:   end.offset,
    cursorPosition:
      focusNode === anchorNode
      ? (anchorOffset < focusOffset
         ? CursorPosition.End
         : CursorPosition.Start)
      :
      endRange.endContainer === focusNode
      ? CursorPosition.End
      : CursorPosition.Start,
  };
}

